import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { updateSection } from "../../../assignmentSlice";
import { useTTS } from "../../../../../hooks/useTTS";
import { getContentHeight } from "../../../../../utils/textUtils";
import StepsBar from "../../widgets/StepsBar";
import { lectureOf } from "./lectureVideo";
import "./ReadingPage.css";

// ECHO 시안 02 — 지문 읽기
// PC: 좌(영어 본문 전체 리스트 + 문장 듣기) / 우(우리말 해석 + 보기·가리기 토글)
// 모바일: 문장 카드(영+한 함께), 현재 카드 teal 테두리 + "이 문장 듣기"
// hide_translation 과제는 출제 의도대로 해석을 아예 제공하지 않는다(토글 없음).
// 강의(question.video_url)가 있으면 PC 는 오른쪽 칸을 "해석 | 강의" 로 바꿔 끼우고,
// 모바일은 "강의" 버튼으로 영상을 문장 목록 위에 붙인다. 지문을 보면서 강의를 듣게 하려는 것이다.
// **강의가 있으면 강의부터 연다**(PC 는 강의 탭, 모바일은 영상을 위에 붙인 채). 아이들이 들어오자마자
// 강의를 보게 하려는 것이다(사장님 결정, 2026-09-28). 탭 순서도 강의가 먼저다.
// PC 에서 강의를 띄우면 오른쪽 해석이 가려지므로 해석을 왼쪽 영어 문장 아래에 작게 붙인다
// (직독직해 화면처럼). 강의를 띄울지는 출제 때 교사가 정한다(question.show_lecture, 기본 켬).
// **강의가 뜨면 해석도 보여 준다.** 해석 숨기기로 낸 과제도 마찬가지다(사장님 결정,
// 2026-09-28: 해석을 가리는 경우는 극히 드물다). PC 는 강의 아래에 지문 삽화도 둔다.
// 영상은 숨길 때 없애지 않고 멈춘다. 다시 열면 보던 자리부터 이어진다.
const FONT =
  "'Pretendard','Spoqa Han Sans Neo','Apple SD Gothic Neo','Noto Sans KR',sans-serif";

const CIRCLED = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";
const numOf = (i) => (i < 20 ? CIRCLED[i] : `${i + 1}.`);

const paneStyle = {
  backgroundColor: "#fff",
  border: "1px solid #E6EDEC", // --line
  borderRadius: "14px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
};
const phStyle = {
  padding: "9px 14px",
  borderBottom: "1px solid #E6EDEC",
  fontSize: "13px",
  fontWeight: 800,
  letterSpacing: "0.06em",
  color: "#00A396", // --teal-deep
  display: "flex",
  alignItems: "center",
  gap: "6px",
};
const listenStyle = {
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  backgroundColor: "#0E141B", // --ink
  color: "#fff",
  fontSize: "12px",
  fontWeight: 700,
  border: "none",
  borderRadius: "999px",
  padding: "6px 12px",
  cursor: "pointer",
};
const tabStyle = (on) => ({
  fontSize: "11px",
  fontWeight: 700,
  padding: "3px 10px",
  borderRadius: "999px",
  border: `1px solid ${on ? "#E5FBF8" : "#E6EDEC"}`,
  backgroundColor: on ? "#E5FBF8" : "#fff", // --teal-soft
  color: on ? "#00A396" : "#7A8490",
  cursor: "pointer",
});
const submitStyle = {
  backgroundColor: "#0E141B",
  color: "#fff",
  border: "none",
  borderRadius: "999px",
  padding: "8px 24px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
};
const pfootStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "10px",
  paddingTop: "10px",
  marginTop: "auto",
};
const pcountStyle = {
  fontSize: "13px",
  color: "#7A8490", // --ink-45
  fontWeight: 700,
  marginRight: "auto",
};

const ReadingPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section, paragraph } = useSelector((state) => state.assignment);
  const [pageList, setPageList] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showKor, setShowKor] = useState(true);
  const [isMobile, setIsMobile] = useState(Math.min(window.innerWidth, window.screen.width) <= 768);
  const currentRef = useRef(null);
  const lectureFrameRef = useRef(null);

  const lecture =
    section.question?.show_lecture === false ? null : lectureOf(section.question?.video_url);
  // 강의가 뜨는 지문은 해석을 가리지 않는다.
  const hideTranslation = !lecture && (section.question?.hide_translation ?? false);
  const illustrationUrl = paragraph?.illustration_url || "";
  // PC 오른쪽 칸: "kor" 해석 / "lecture" 강의.
  const [rightTab, setRightTab] = useState(lecture ? "lecture" : "kor");
  const [showMobileLecture, setShowMobileLecture] = useState(Boolean(lecture));

  // 지문을 바꾸면 그 지문에 강의가 있는지로 다시 정한다. `section` 이 아니라 `section.id` 에
  // 건다. 진도를 저장할 때마다 `section` 이 통째로 바뀌어서, `section` 에 걸면 보던 탭이 튄다.
  const hasLecture = Boolean(lecture);
  useEffect(() => {
    setRightTab(hasLecture ? "lecture" : "kor");
    setShowMobileLecture(hasLecture);
  }, [section?.id, hasLecture]);

  useEffect(() => {
    if (!section) return;
    setPageList(section.question.data);
  }, [section]);

  useEffect(() => {
    const onResize = () => setIsMobile(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [currentIndex]);

  const { speak, stopSpeaking } = useTTS();

  // 소리가 둘 겹치지 않게 한다. 문장 듣기를 누르면 강의를 멈춘다.
  const pauseLecture = () => {
    const frame = lectureFrameRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
      "*"
    );
  };

  const openLecture = () => {
    stopSpeaking();
    setRightTab("lecture");
    setShowMobileLecture(true);
  };

  const closeLecture = () => {
    pauseLecture();
    setRightTab("kor");
    setShowMobileLecture(false);
  };

  const lectureBlock = (pinned, visible) =>
    lecture && (
      <div
        className={`reading-lecture${pinned ? " reading-lecture--pinned" : ""}`}
        hidden={!visible}
      >
        {lecture.kind === "youtube" ? (
          <div className="reading-lecture__frame">
            <iframe
              ref={lectureFrameRef}
              src={lecture.embedUrl}
              title="지문 강의"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        ) : (
          <a className="reading-lecture__link" href={lecture.url} target="_blank" rel="noopener noreferrer">
            ▶ 강의 보기 (새 창)
          </a>
        )}
        {!pinned && (
          <p className="reading-lecture__caption">
            지문을 보면서 강의를 들어 보세요. 해석은 왼쪽 문장 아래에 작게 붙여 두었어요.
          </p>
        )}
        {/* PC 만. 강의 아래 빈자리에 지문 삽화를 둔다. 모바일은 영상이 위에 붙어 있어 넣지 않는다. */}
        {!pinned && illustrationUrl && (
          <img
            className="reading-lecture__illustration"
            src={illustrationUrl}
            alt="지문 삽화"
            onClick={() => window.open(illustrationUrl, "_blank")}
          />
        )}
      </div>
    );

  const handleSelect = (idx) => {
    pauseLecture();
    stopSpeaking();
    setCurrentIndex(idx);
    speak({ text: pageList[idx][0] });
  };

  const handlePlay = () => {
    pauseLecture();
    stopSpeaking();
    if (pageList[currentIndex]) speak({ text: pageList[currentIndex][0] });
  };

  const handleNext = () => {
    stopSpeaking();
    if (currentIndex < pageList.length - 1) {
      const next = currentIndex + 1;
      speak({ text: pageList[next][0] });
      setCurrentIndex(next);
    } else {
      if (!isPreview && !isReview) {
        dispatch(
          updateSection({ sectionId: section.id, data: { is_finished: true } })
        );
      }
      alert("학습이 완료되었습니다.");
      setCurrentStep(2);
    }
  };

  const footer = (
    <div style={pfootStyle}>
      <span style={pcountStyle}>
        {pageList.length > 0 ? currentIndex + 1 : 0} / {pageList.length}
        {isMobile ? "" : " 문장"}
      </span>
      <button style={submitStyle} onClick={handleNext}>
        다음 →
      </button>
    </div>
  );

  // ── 모바일: 문장 카드(영+한 함께) ──
  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: getContentHeight(true),
          fontFamily: FONT,
          backgroundColor: "#FFFFFF",
        }}
      >
        <StepsBar />
        <div style={{ flex: 1, minHeight: 0, padding: "12px", display: "flex" }}>
          <div style={{ ...paneStyle, flex: 1 }}>
            <div style={phStyle}>
              지문 읽기
              {lecture && (
                <button
                  className={`reading-lecture-toggle${showMobileLecture ? " reading-lecture-toggle--on" : ""}`}
                  style={{ marginLeft: "auto" }}
                  onClick={showMobileLecture ? closeLecture : openLecture}
                >
                  {showMobileLecture ? "강의 닫기" : "▶ 강의"}
                </button>
              )}
              {!hideTranslation && (
                <span style={{ marginLeft: lecture ? "4px" : "auto", display: "flex", gap: "4px" }}>
                  <button style={tabStyle(showKor)} onClick={() => setShowKor(true)}>
                    해석 보기
                  </button>
                  <button style={tabStyle(!showKor)} onClick={() => setShowKor(false)}>
                    가리기
                  </button>
                </span>
              )}
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {lectureBlock(true, showMobileLecture)}
              {pageList.map((sentence, idx) => {
                const isOn = idx === currentIndex;
                return (
                  <div
                    key={idx}
                    ref={isOn ? currentRef : null}
                    onClick={() => handleSelect(idx)}
                    style={{
                      // 시안 02 모바일 카드: 현재 = teal 테두리 + teal-mist 배경
                      border: isOn ? "1.5px solid #00D9C0" : "1px solid #E6EDEC",
                      backgroundColor: isOn ? "#F2FAF8" : "#fff",
                      borderRadius: "10px",
                      padding: "10px 12px",
                      marginBottom: "8px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontSize: "15px", lineHeight: 1.7, color: "#0E141B" }}>
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 800,
                          color: "#00A396",
                          verticalAlign: "super",
                          marginRight: "3px",
                        }}
                      >
                        {numOf(idx)}
                      </span>
                      {sentence[0]}
                    </div>
                    {!hideTranslation && showKor && sentence[1] && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#3A434E",
                          lineHeight: 1.75,
                          paddingTop: "5px",
                        }}
                      >
                        {sentence[1]}
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                style={{ ...listenStyle, margin: "4px auto 0", display: "flex" }}
                onClick={handlePlay}
              >
                ▶ 이 문장 듣기
              </button>
              {footer}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── PC: 좌우 두 패널 ──
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: getContentHeight(false),
        fontFamily: FONT,
        backgroundColor: "#FFFFFF",
      }}
    >
      <StepsBar />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          padding: "16px 20px",
          display: "flex",
          gap: "12px",
          maxWidth: "1160px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* 영어 본문 패널 */}
        <div style={{ ...paneStyle, flex: 1.1 }}>
          <div style={phStyle}>
            영어 본문
            <button style={listenStyle} onClick={handlePlay}>
              ▶ 문장 듣기
            </button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "12px 15px" }}>
            {pageList.map((sentence, idx) => {
              const isOn = idx === currentIndex;
              return (
                <div
                  key={idx}
                  ref={isOn ? currentRef : null}
                  onClick={() => handleSelect(idx)}
                  style={{
                    // 시안 02 read-sent(.on = --teal-soft)
                    padding: "8px 10px",
                    borderRadius: "8px",
                    fontSize: "16px",
                    lineHeight: 1.7,
                    cursor: "pointer",
                    color: "#0E141B",
                    backgroundColor: isOn ? "#E5FBF8" : "transparent",
                  }}
                >
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 800,
                      color: "#00A396",
                      verticalAlign: "super",
                      marginRight: "3px",
                    }}
                  >
                    {numOf(idx)}
                  </span>
                  {sentence[0]}
                  {rightTab === "lecture" && sentence[1] && (
                    <div className="reading-lecture-kor">{sentence[1]}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* 우리말 해석 패널 — hide_translation 과제에서는 해석을 렌더링하지 않음.
            강의가 있으면 이 칸을 "해석 | 강의" 로 바꿔 끼운다. */}
        {!hideTranslation ? (
          <div style={{ ...paneStyle, flex: 1 }}>
            <div style={phStyle}>
              {lecture ? (
                <span style={{ display: "flex", gap: "4px" }}>
                  <button style={tabStyle(rightTab === "lecture")} onClick={openLecture}>
                    ▶ 강의
                  </button>
                  <button style={tabStyle(rightTab === "kor")} onClick={closeLecture}>
                    우리말 해석
                  </button>
                </span>
              ) : (
                "우리말 해석"
              )}
              {rightTab === "kor" && (
                <span style={{ marginLeft: "auto", display: "flex", gap: "4px" }}>
                  <button style={tabStyle(showKor)} onClick={() => setShowKor(true)}>
                    보기
                  </button>
                  <button style={tabStyle(!showKor)} onClick={() => setShowKor(false)}>
                    가리기
                  </button>
                </span>
              )}
            </div>
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: "12px 15px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {lectureBlock(false, rightTab === "lecture")}
              {rightTab === "lecture" ? null : showKor ? (
                pageList.map((sentence, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelect(idx)}
                    style={{
                      // 시안 02 kor-line(.on = --sage)
                      fontSize: "14px",
                      color: "#3A434E",
                      lineHeight: 1.75,
                      padding: "8px 10px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor:
                        idx === currentIndex ? "#F4F8F7" : "transparent",
                    }}
                  >
                    {sentence[1]}
                  </div>
                ))
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#B9C1C9",
                    fontSize: "14px",
                  }}
                >
                  해석을 가렸습니다
                </div>
              )}
              {footer}
            </div>
          </div>
        ) : (
          <div
            style={{
              ...paneStyle,
              flex: 1,
              border: "none",
              backgroundColor: "transparent",
              justifyContent: "flex-end",
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReadingPage;
